import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// PrimeNG Modules
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { IftaLabelModule } from 'primeng/iftalabel';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem, MessageService } from 'primeng/api';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { PosmProject } from '../models/posm-project.model';
import { PosmProjectItem } from '../models/posm-project-item.model';
import { PosmDeliverySchedule, PosmProjectStore } from '../models/posm-execution.models';
import { LocationMatchPreview, PosmService } from '../posm.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { CompanyService } from '../../company/company.service';
import { CustomerService } from '../../customer/customer.service';
import { ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { saveAs } from 'file-saver';
import { finalize, forkJoin, of } from 'rxjs';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { WarehousingItem, WarehousingMasterData, WarehousingStockSummary } from '../../warehousing/warehousing.model';
import { CrmService } from '../../crm/crm.service';
import { CrmAccount, CrmClient, CrmClientCommunication, CrmMerchandiser } from '../../crm/crm.model';
import { Customer } from '../../customer/customer.model';
import { PosmSetupData } from '../models/posm-planning.model';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { WarehousingItemFormComponent } from '../../warehousing/items/warehousing-item-form.component';
import { WarehousingMasterDataFormComponent } from '../../warehousing/billing-config/warehousing-master-data-form.component';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../shared/crm-quick-create/crm-quick-create-dialog.component';

type WarehousingItemOption = WarehousingItem & {
    displayName: string;
    itemMeta: string;
    sizeLabel: string;
};
type WarehousingWarehouseOption = {
    id: number;
    name: string;
    code?: string;
    shortDescription?: string;
    externalLocationReference?: string;
    warehousingWarehouseId: number;
    warehouseOwnershipType?: 'COMPANY' | 'THIRD_PARTY_VIRTUAL' | string;
    supplierClientId?: number;
    supplierWarehouseName?: string;
};
type JobTypeGroupOption = {
    label: string;
    value: string;
};
type PosmClientContactOption = {
    id?: number | null;
    displayName: string;
    name: string;
    emailAddress?: string;
    phoneNumber?: string;
    mobileNumber?: string;
    primaryRecord?: boolean;
};

@Component({
    selector: 'app-create-posm-project',
    imports: [
        CommonModule, ReactiveFormsModule, FormsModule, FieldsetModule, InputTextModule,
        ButtonModule, InputGroupModule, InputGroupAddonModule,
        SelectModule, FileUploadModule, InputNumberModule, IftaLabelModule,
        MultiSelectModule,
        BreadcrumbModule, FontAwesomeModule, ToastModule, DatePickerModule,
        SelectButtonModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageFormSectionComponent, CommercialPartyPickerComponent
    ],
    templateUrl: './create-posm-project.component.html',
    styleUrls: ['./create-posm-project.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DialogService]
})
export class CreatePosmProjectComponent implements OnInit {

    editingProjectId: number | null = null;
    importSchedulesMode = false;
    editingProjectLoaded = signal(false);
    editingProject = signal<PosmProject | null>(null);

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    createProjectForm!: FormGroup;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    crmClients = signal<CrmClient[]>([]);
    accounts = signal<CrmAccount[]>([]);
    merchandisers = signal<CrmMerchandiser[]>([]);
    customers = signal<Customer[]>([]);
    crmClientContacts = signal<PosmClientContactOption[]>([]);
    brandOptions = signal<WarehousingMasterData[]>([]);
    warehousingItemOptions = signal<WarehousingItemOption[]>([]);
    stockSummary = signal<WarehousingStockSummary[]>([]);
    priorityOptions = signal<any[]>([]);
    regionOptions = signal<any[]>([]);
    jobTypeOptions = signal<PosmSetupData[]>([]);
    selectedJobTypeGroup = signal<string | null>(null);
    warehouseOptions = signal<WarehousingWarehouseOption[]>([]);
    managerOptions = signal<any[]>([]);
    strategyOptions = signal<PosmSetupData[]>([]);
    supplierOptions = signal<any[]>([]);
    collectionOptions = signal<any[]>([]);
    crmClientOptions = computed(() => this.crmClients().map(client => ({
        ...client,
        displayName: this.crmClientDisplayName(client)
    })));
    customerOptions = computed(() => this.customers().map(customer => ({
        ...customer,
        displayName: this.customerDisplayName(customer)
    })));
    clientContactOptions = computed(() => this.crmClientContacts());
    merchandiserOptions = computed(() => this.merchandisers().map(merchandiser => ({
        ...merchandiser,
        displayName: this.merchandiserDisplayName(merchandiser)
    })));
    jobTypeGroupOptions = computed<JobTypeGroupOption[]>(() => {
        const seen = new Set<string>();
        return this.jobTypeOptions()
            .map(row => this.jobTypeGroupValue(row))
            .filter((group): group is string => !!group)
            .filter(group => {
                const key = this.normalizeReference(group);
                if (!key || seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            })
            .map(group => ({ label: group, value: group }));
    });
    filteredJobTypeOptions = computed<PosmSetupData[]>(() => {
        const group = this.selectedJobTypeGroup();
        if (!group) {
            return this.jobTypeOptions();
        }
        return this.jobTypeOptions().filter(row => this.jobTypeGroupValue(row) === group);
    });

    projectItems = signal<PosmProjectItem[]>([]);
    uploadedStores = signal<PosmProjectStore[]>([]);
    locationMatches = signal<LocationMatchPreview[]>([]);
    deliveryScheduleRows = signal<PosmDeliverySchedule[]>([]);
    owningCompanyId = signal<number | null>(null);
    isSaving = signal<boolean>(false);
    isParsing = signal<boolean>(false);
    isMasterDataLoading = signal<boolean>(false);
    isClientContactsLoading = signal<boolean>(false);
    dropdownValuesLoading = computed(() => this.isMasterDataLoading() || this.isClientContactsLoading());
    quickCreateTargetItem: PosmProjectItem | null = null;
    private itemDialogRef?: DynamicDialogRef;
    private warehouseDialogRef?: DynamicDialogRef;

    constructor(
        private fb: FormBuilder,
        private posmService: PosmService,
        private sessionStorageService: SessionStorageService,
        private companyService: CompanyService,
        private customerService: CustomerService,
        private warehousingService: WarehousingService,
        private crmService: CrmService,
        private logisticsProjectsService: LogisticsProjectsService,
        private route: ActivatedRoute,
        private router: Router,
        private messageService: MessageService,
        private dialogService: DialogService
    ) { }

    ngOnInit(): void {
        const editId = Number(this.route.snapshot.paramMap.get('id'));
        this.editingProjectId = editId > 0 ? editId : null;
        this.importSchedulesMode = !!this.route.snapshot.data['importSchedules'];
        this.breadcrumbItems = [
            { label: 'POSM Executions', routerLink: '/posm/executions' },
            { label: this.importSchedulesMode ? 'Import Delivery Schedules' : this.editingProjectId ? 'Edit Execution' : 'New Execution' }
        ];

        const customer = this.sessionStorageService.getCustomer();
        if (customer?.company?.id) {
            this.owningCompanyId.set(customer.company.id);
        }

        this.initForm();
        this.loadMasterData();
        if (!this.editingProjectId) this.addNewStandItem();
    }

    initForm(): void {
        this.createProjectForm = this.fb.group({
            projectName: ['', Validators.required],
            crmClient: [null as CrmClient | null, Validators.required],
            clientAccountId: [null, Validators.required],
            brandId: [null],
            clientContact: [null as PosmClientContactOption | null],
            jobPriority: [null, Validators.required],
            regionId: [null],
            jobTypeGroup: [null, Validators.required],
            jobTypeIds: [[], Validators.required],
            manager: [null, Validators.required],
            warehouse: [null],
            supervisor: [null],
            crmMerchandiser: [null as CrmMerchandiser | null],
            startDate: [new Date(), Validators.required],
            deadline: [null, Validators.required],
            executionStrategy: [null],
            costEstimation: [0],
            supplier: [null],
            collectionPreference: [null]
        });
        this.createProjectForm.get('crmClient')?.valueChanges.subscribe(client => this.onProjectClientChanged(client));
        this.createProjectForm.get('clientAccountId')?.valueChanges.subscribe(accountId => this.onProjectAccountChanged(accountId));
        this.createProjectForm.get('jobTypeGroup')?.valueChanges.subscribe(group => this.onJobTypeGroupChanged(group));
        this.createProjectForm.get('jobTypeIds')?.valueChanges.subscribe(jobTypeIds => this.syncJobTypeGroupFromTypes(jobTypeIds));
        if (this.editingProjectId) {
            for (const field of ['projectName', 'crmClient', 'clientAccountId', 'clientContact', 'jobTypeGroup', 'jobTypeIds']) {
                this.createProjectForm.get(field)?.disable({ emitEvent: false });
            }
        }

    }

    loadMasterData(): void {
        const companyId = this.owningCompanyId();
        if (!companyId) return;

        this.isMasterDataLoading.set(true);
        forkJoin({
            companies: this.editingProjectId ? of([]) : this.companyService.getCompaniesByCountry(1),
            warehouses: this.editingProjectId && !this.importSchedulesMode ? of({ data: [] }) : this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }),
            managers: this.customerService.getCustomersByCompany(companyId),
            accounts: this.editingProjectId && !this.importSchedulesMode ? of([]) : this.crmService.getAccounts(companyId),
            merchandisers: this.editingProjectId ? of([]) : this.crmService.getMerchandisers(companyId),
            crmClients: this.editingProjectId && !this.importSchedulesMode ? of([]) : this.crmService.getClients(companyId),
            warehousingItems: this.editingProjectId && !this.importSchedulesMode ? of({ data: [] }) : this.warehousingService.getItems({ activeStatus: 'ACTIVE' }),
            stockSummary: this.editingProjectId && !this.importSchedulesMode ? of({ data: [] }) : this.warehousingService.getStockSummary({ activeStatus: 'ACTIVE' }),
            brands: this.editingProjectId && !this.importSchedulesMode ? of({ data: [] }) : this.warehousingService.getMasterData('BRAND'),
            regions: this.editingProjectId ? of({ data: [] }) : this.warehousingService.getMasterData('REGION'),
            jobTypes: this.editingProjectId ? of([]) : this.posmService.getSetup('JOB_TYPE', companyId),
            jobPriorities: this.posmService.getSetup('JOB_PRIORITY', companyId),
            strategies: this.editingProjectId ? of([]) : this.posmService.getSetup('EXECUTION_STRATEGY', companyId),
            collectionPreferences: this.editingProjectId ? of([]) : this.posmService.getSetup('COLLECTION_PREFERENCE', companyId)
        }).pipe(
            finalize(() => this.isMasterDataLoading.set(false))
        ).subscribe({
            next: ({ companies, warehouses, managers, accounts, merchandisers, crmClients, warehousingItems, stockSummary, brands, regions, jobTypes, jobPriorities, strategies, collectionPreferences }: any) => {
                this.supplierOptions.set(companies || []);
                this.warehouseOptions.set(this.toWarehouseOptions(warehouses?.data || []));
                this.managerOptions.set(managers || []);
                this.customers.set(managers || []);
                this.accounts.set(accounts || []);
                this.merchandisers.set(merchandisers || []);
                this.crmClients.set(crmClients || []);
                this.warehousingItemOptions.set((warehousingItems?.data || []).map((item: WarehousingItem) => this.toWarehousingItemOption(item)));
                this.stockSummary.set(stockSummary?.data || []);
                this.brandOptions.set(brands?.data || []);
                this.regionOptions.set(regions?.data || []);
                const normalizedJobTypes = this.normalizeJobTypeOptions(jobTypes || []);
                this.jobTypeOptions.set(normalizedJobTypes);
                this.priorityOptions.set((jobPriorities || []).map((row: PosmSetupData) => ({
                    label: row.name,
                    value: row.code || row.name
                })));
                this.strategyOptions.set(strategies || []);
                this.collectionOptions.set((collectionPreferences || []).map((row: PosmSetupData) => ({
                    label: row.name,
                    value: row.code || row.name
                })));

                const currentCustomer = this.sessionStorageService.getCustomer();
                const currentManager = (managers || []).find((row: any) => row.id === currentCustomer?.id) || currentCustomer || null;
                const defaultJobType = normalizedJobTypes[0] || null;
                const defaultJobTypeGroup = defaultJobType ? this.jobTypeGroupValue(defaultJobType) : null;
                this.createProjectForm.patchValue({
                    manager: currentManager,
                    jobPriority: jobPriorities?.[0]?.code || jobPriorities?.[0]?.name || null,
                    jobTypeGroup: defaultJobTypeGroup,
                    jobTypeIds: defaultJobType?.id ? [defaultJobType.id] : [],
                    executionStrategy: strategies?.[0]?.id || null,
                    collectionPreference: collectionPreferences?.[0]?.code || collectionPreferences?.[0]?.name || null
                }, { emitEvent: false });
                this.selectedJobTypeGroup.set(defaultJobTypeGroup);
                if (this.editingProjectId) this.loadProjectForEdit(this.editingProjectId);
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Loading failed',
                    detail: 'Some POSM dropdown values could not be loaded.'
                });
            }
        });
    }

    private loadProjectForEdit(id: number): void {
        const companyId = this.owningCompanyId();
        if (!companyId) return;
        this.posmService.getProject(id, companyId).subscribe({
            next: project => {
                this.editingProject.set(project);
                const manager = this.managerOptions().find(row => row.id === project.manager?.id) || project.manager || null;
                this.createProjectForm.patchValue({
                    jobPriority: project.jobPriority || null,
                    startDate: project.startDate ? new Date(project.startDate) : null,
                    deadline: project.deadline ? new Date(project.deadline) : null,
                    manager
                }, { emitEvent: false });
                this.editingProjectLoaded.set(true);
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Execution not loaded',
                detail: err?.error?.message || 'Unable to load this POSM execution.' })
        });
    }

    addNewStandItem(): void {
        const current = this.projectItems();
        this.projectItems.set([...current, {
            inventoryItem: null,
            quantity: 1,
            sourceWarehouse: null
        }]);
    }

    removeStandItem(index: number): void {
        const current = this.projectItems();
        current.splice(index, 1);
        this.projectItems.set([...current]);
    }

    openWarehousingItemCreate(item?: PosmProjectItem): void {
        this.quickCreateTargetItem = item || null;
        const crmClientId = this.createProjectForm.value.crmClient?.id;
        const crmAccountId = this.createProjectForm.value.clientAccountId;
        this.itemDialogRef?.close();
        this.itemDialogRef = this.dialogService.open(WarehousingItemFormComponent, {
            header: 'New Warehousing Item',
            modal: true,
            width: '96vw',
            breakpoints: { '1400px': '96vw', '960px': '98vw', '640px': '100vw' },
            data: {
                crmClientId,
                crmAccountId
            },
            styleClass: 'posm-warehousing-item-dialog'
        });
        this.itemDialogRef.onClose.subscribe((created: WarehousingItem | undefined) => {
            if (created?.id) {
                this.handleDialogWarehouseItemCreated(created);
                return;
            }
            this.quickCreateTargetItem = null;
        });
    }

    openWarehousingWarehouseCreate(item?: PosmProjectItem): void {
        this.quickCreateTargetItem = item || null;
        this.warehouseDialogRef?.close();
        this.warehouseDialogRef = this.dialogService.open(WarehousingMasterDataFormComponent, {
            header: 'New Source Warehouse',
            modal: true,
            width: '96vw',
            breakpoints: { '1400px': '96vw', '960px': '98vw', '640px': '100vw' },
            data: {
                type: 'WAREHOUSE'
            },
            styleClass: 'posm-warehouse-dialog'
        });
        this.warehouseDialogRef.onClose.subscribe((created: WarehousingMasterData | undefined) => {
            if (created?.id) {
                this.handleDialogWarehouseCreated(created);
                return;
            }
            this.quickCreateTargetItem = null;
        });
    }

    onProjectClientChanged(client: CrmClient | null): void {
        const accounts = this.accountsForClient(client?.id);
        const selectedAccountId = this.createProjectForm.value.clientAccountId;
        const nextAccountId = accounts.some(account => account.id === selectedAccountId)
            ? selectedAccountId
            : null;
        this.createProjectForm.patchValue({
            clientAccountId: nextAccountId,
            clientContact: null
        }, { emitEvent: false });
        this.loadCrmClientContacts(client);
        this.onProjectAccountChanged(nextAccountId);
        this.syncProjectItemsToClientContext();
    }

    onProjectAccountChanged(accountId: number | null | undefined): void {
        const currentContact = this.createProjectForm.value.clientContact as PosmClientContactOption | null;
        this.createProjectForm.patchValue({
            clientContact: currentContact || this.resolveDefaultClientContact(this.createProjectForm.value.crmClient, accountId)
        }, { emitEvent: false });
        this.syncProjectItemsToClientContext();
    }

    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') {
            this.crmClients.update(clients => [...clients, result.record]);
        } else if (result.mode === 'account') {
            this.accounts.update(accounts => [...accounts, result.record]);
        } else {
            this.crmClientContacts.update(contacts => [...contacts, this.toClientContactOption(result.record)]);
        }
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        this.createProjectForm.patchValue({
            crmClient: this.crmClients().find(item => item.id === reference.crmClientId) || null,
            clientAccountId: reference.crmAccountId ?? null,
            clientContact: this.crmClientContacts().find(item => item.id === reference.crmContactId) || null
        });
    }

    onJobTypeGroupChanged(group: string | null | undefined): void {
        const normalizedGroup = group || null;
        this.selectedJobTypeGroup.set(normalizedGroup);
        const allowedTypes = this.jobTypesForGroup(normalizedGroup);
        const currentJobTypeIds = Array.isArray(this.createProjectForm.get('jobTypeIds')?.value)
            ? (this.createProjectForm.get('jobTypeIds')?.value as number[])
            : [];
        const nextJobTypeIds = currentJobTypeIds.filter(jobTypeId => allowedTypes.some(row => row.id === jobTypeId));
        if (!nextJobTypeIds.length && allowedTypes[0]?.id) {
            nextJobTypeIds.push(allowedTypes[0].id);
        }
        if (nextJobTypeIds.length !== currentJobTypeIds.length
                || nextJobTypeIds.some((jobTypeId, index) => jobTypeId !== currentJobTypeIds[index])) {
            this.createProjectForm.patchValue({
                jobTypeIds: nextJobTypeIds
            }, { emitEvent: false });
        }
    }

    accountsForClient(clientId: number | null | undefined): CrmAccount[] {
        return this.accounts().filter(account => !clientId || account.crmClientId === clientId);
    }

    merchandisersForSelectedAccount(): CrmMerchandiser[] {
        const selected = this.createProjectForm?.value?.crmMerchandiser as CrmMerchandiser | null;
        return selected?.id
            ? this.merchandiserOptions().filter(merchandiser => merchandiser.id === selected.id)
            : [];
    }

    cancel(): void {
        this.router.navigate(this.editingProjectId
            ? ['/posm/executions', this.editingProjectId] : ['/posm/executions'],
            this.importSchedulesMode ? { queryParams: { tab: 'delivery-schedule' } } : {});
    }

    saveDraft(): void {
        if (!this.ensureCrmClientAccountSelected()) return;
        this.submitProject('Draft');
    }

    createProject(): void {
        if (this.importSchedulesMode) {
            this.importSchedules();
            return;
        }
        if (this.editingProjectId) {
            this.saveHeader();
            return;
        }
        if (!this.ensureCrmClientAccountSelected()) return;
        if (this.createProjectForm.invalid) {
            this.createProjectForm.markAllAsTouched();
            this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Please fill all required fields' });
            return;
        }
        this.submitProject('NEW');
    }

    private saveHeader(): void {
        if (!this.editingProjectId || !this.editingProjectLoaded()) return;
        if (this.createProjectForm.invalid) {
            this.createProjectForm.markAllAsTouched();
            this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Fill all required header fields.' });
            return;
        }
        const form = this.createProjectForm.value;
        this.isSaving.set(true);
        this.posmService.updateProjectHeader(this.editingProjectId, this.owningCompanyId()!, {
            jobPriority: form.jobPriority,
            startDate: form.startDate,
            deadline: form.deadline,
            manager: this.customerReference(form.manager)
        }).pipe(finalize(() => this.isSaving.set(false))).subscribe({
            next: project => this.router.navigate(['/posm/executions', project.id]),
            error: err => this.messageService.add({ severity: 'error', summary: 'Header not saved',
                detail: err?.error?.message || err?.error?.detail || 'Unable to update the execution header.' })
        });
    }

    private ensureCrmClientAccountSelected(): boolean {
        const crmClient = this.createProjectForm.value.crmClient as CrmClient | null;
        const clientAccountId = this.createProjectForm.value.clientAccountId as number | null;
        if (crmClient?.id && clientAccountId) return true;

        this.createProjectForm.get('crmClient')?.markAsTouched();
        this.createProjectForm.get('clientAccountId')?.markAsTouched();
        this.messageService.add({
            severity: 'error',
            summary: 'CRM account required',
            detail: 'Select a CRM Client and CRM Client Account before saving this POSM execution.'
        });
        return false;
    }

    downloadTemplate(): void {
        this.posmService.downloadTemplate().subscribe({
            next: (blob) => {
                saveAs(blob, 'posm-delivery-schedule-template.xlsx');
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download template' });
            }
        });
    }

    onUploadStoreList(event: any): void {
        const file = event.files[0];
        if (file) {
            const companyId = this.owningCompanyId();
            if (!companyId) {
                this.messageService.add({ severity: 'error', summary: 'Owning company required', detail: 'Select an owning company before uploading a delivery schedule.' });
                return;
            }
            this.isParsing.set(true);
            this.uploadedStores.set([]);
            this.deliveryScheduleRows.set([]);
            this.locationMatches.set([]);
            const previewRequest = this.importSchedulesMode && this.editingProjectId
                ? this.posmService.previewProjectScheduleImport(this.editingProjectId, file, companyId)
                : this.posmService.parseDeliverySchedule(file, companyId);
            previewRequest.subscribe({
                next: (preview) => {
                    const data = preview.rows || [];
                    const missingUCode = data.find(row => !row.uCode?.trim());
                    if (missingUCode) {
                        this.isParsing.set(false);
                        this.messageService.add({ severity: 'error', summary: 'Delivery schedule upload rejected',
                            detail: `${missingUCode.sourceSheetName || 'Sheet'} row ${missingUCode.sourceRowNumber || '?'}: U Code is required. Download the current template and upload it again.` });
                        return;
                    }
                    const stores = this.buildUploadedStores(data, companyId);
                    const resolvedItems = this.resolveUploadedItems(data, companyId);
                    this.uploadedStores.set(stores);
                    const schedules = this.buildDeliveryScheduleRows(data, stores, companyId, resolvedItems.rowItemMatches);
                    this.deliveryScheduleRows.set(schedules);
                    if (resolvedItems.items.length) {
                        this.projectItems.set(resolvedItems.items);
                    }
                    this.showItemResolutionWarnings(resolvedItems);
                    this.posmService.previewLocationMatches(companyId, stores.map(store => ({
                        name: store.location?.name, reference: store.location?.externalLocationReference || store.storeNumber,
                        city: store.city, retailer: store.contactPersonName
                    }))).pipe(finalize(() => this.isParsing.set(false))).subscribe({
                        next: matches => {
                            this.locationMatches.set(matches);
                            stores.forEach((store, index) => {
                                const match = matches[index];
                                store.locationMatchFingerprint = match.fingerprint;
                                if (match.exactLocationId) this.chooseLocation(store, match.exactLocationId);
                                else if (!match.requiresReview) this.chooseLocation(store, -1);
                            });
                            this.uploadedStores.set([...stores]);
                            this.messageService.add({ severity: 'success', summary: 'Workbook ready',
                                detail: `Parsed ${data.length} rows into ${schedules.length} schedules and ${stores.length} locations. Review suggested matches.` });
                        },
                        error: () => {
                            this.uploadedStores.set([]);
                            this.deliveryScheduleRows.set([]);
                            this.messageService.add({ severity: 'error', summary: 'Location review failed',
                                detail: 'Could not check existing locations. Upload the workbook again.' });
                        }
                    });
                },
                error: (err) => {
                    this.isParsing.set(false);
                    this.messageService.add({ severity: 'error', summary: 'Delivery schedule upload rejected',
                        detail: err?.error?.message || err?.error?.detail || 'Failed to parse Excel file' });
                }
            });
        }
    }

    clearUploadedSchedule(upload?: FileUpload | null): void {
        upload?.clear();
        this.uploadedStores.set([]);
        this.deliveryScheduleRows.set([]);
        this.locationMatches.set([]);
        this.projectItems.set([]);
        if (!this.importSchedulesMode) this.addNewStandItem();
    }

    private importSchedules(): void {
        const projectId = this.editingProjectId;
        const companyId = this.owningCompanyId();
        if (!projectId || !companyId || !this.editingProjectLoaded() || this.isSaving() || !this.deliveryScheduleRows().length) return;
        if (!this.locationReviewComplete()) return;
        if (this.projectItems().some(item => !item.inventoryItem?.id || !item.sourceWarehouse?.id)) {
            this.messageService.add({ severity: 'warn', summary: 'Warehousing item required', detail: 'Resolve each imported item and warehouse before importing.' });
            return;
        }
        const additions: PosmProject = {
            stores: this.uploadedStores().map(store => ({ ...store, owningCompanyId: companyId })),
            deliverySchedules: this.deliveryScheduleRows().map(({ importGroupKey, ...row }) => ({
                ...row, standQty: this.toPositiveQuantity(row.standQty),
                inventoryItem: this.warehousingItemReference(row.inventoryItem), owningCompanyId: companyId
            })),
            items: this.projectItems().map(({ missingItemReference, importGroupKey, brandId, brandName, ...item }) => ({
                ...item, inventoryItem: this.warehousingItemReference(item.inventoryItem), sourceWarehouse: undefined,
                sourceWarehousingWarehouseId: item.sourceWarehouse?.warehousingWarehouseId || item.sourceWarehouse?.id,
                owningCompanyId: companyId
            }))
        };
        this.isSaving.set(true);
        this.posmService.importProjectSchedules(projectId, companyId, additions).pipe(finalize(() => this.isSaving.set(false))).subscribe({
            next: () => this.router.navigate(['/posm/executions', projectId], { queryParams: { tab: 'delivery-schedule' } }),
            error: err => this.messageService.add({ severity: 'error', summary: 'Schedules not imported',
                detail: err?.error?.message || err?.error?.detail || 'Unable to import the delivery schedules.' })
        });
    }

    private submitProject(status: string): void {
        if (!this.locationReviewComplete()) return;
        const unresolvedItemCount = this.projectItems()
            .filter(item => !item.inventoryItem?.id && (!!item.missingItemReference || !!item.sourceWarehouse?.id))
            .length;
        if (unresolvedItemCount) {
            this.messageService.add({ severity: 'warn', summary: 'Warehousing item required', detail: 'Create or select the missing Warehousing item before saving the project.' });
            return;
        }

        this.isSaving.set(true);
        const formValues = this.createProjectForm.value;
        const clientContact = formValues.clientContact as PosmClientContactOption | null;
        const selectedJobTypes = this.selectedJobTypes(formValues.jobTypeIds);
        const selectedJobType = selectedJobTypes[0];
        const selectedJobTypeGroup = selectedJobType ? this.jobTypeGroupValue(selectedJobType) : formValues.jobTypeGroup;
        const selectedStrategy = this.strategyOptions().find(row => row.id === formValues.executionStrategy);
        const validItems = this.projectItems().filter(item => item.inventoryItem?.id);
        const uniqueBrandIds = Array.from(new Set(
            validItems
                .map(item => item.brandId || item.inventoryItem?.brandId)
                .filter((value): value is number => value != null)
        ));
        const derivedBrandId = uniqueBrandIds.length === 1 ? uniqueBrandIds[0] : null;
        const derivedBrand = this.brandOptions().find(row => row.id === derivedBrandId);

        const newProject: PosmProject = {
            projectName: formValues.projectName,
            client: this.commercialPartyReference.legacyCompanyId
                ? { id: this.commercialPartyReference.legacyCompanyId } : undefined,
            crmClient: this.crmClientReference(formValues.crmClient),
            crmClientId: formValues.crmClient?.id,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? undefined,
            brandId: derivedBrandId || undefined,
            brandName: derivedBrand?.name,
            clientAccountId: formValues.clientAccountId,
            clientContact: undefined,
            clientContactId: this.commercialPartyReference.legacyCustomerId ?? undefined,
            clientContactName: clientContact?.name,
            clientContactPhone: clientContact?.phoneNumber || clientContact?.mobileNumber,
            clientContactEmail: clientContact?.emailAddress,
            manager: this.customerReference(formValues.manager),
            startDate: formValues.startDate,
            deadline: formValues.deadline,
            jobPriority: formValues.jobPriority,
            regionId: formValues.regionId?.id,
            jobTypeGroup: selectedJobTypeGroup || undefined,
            jobType: selectedJobType?.name,
            jobTypes: selectedJobTypes.map(row => row.name),
            jobTypeCodes: selectedJobTypes.map(row => row.code || row.name),
            executionStrategy: selectedStrategy?.name,
            costEstimation: formValues.costEstimation,
            supplier: this.companyReference(formValues.supplier),
            collectionPreference: formValues.collectionPreference,
            status: status,
            owningCompanyId: this.owningCompanyId() || 0,
            logisticsProjectId: this.requirementProjectId() || undefined,
            stores: this.uploadedStores().map(store => ({
                ...store,
                owningCompanyId: this.owningCompanyId() || 0
            })),
            deliverySchedules: this.deliveryScheduleRows().map(({ importGroupKey, ...schedule }) => ({
                ...schedule,
                inventoryItem: this.warehousingItemReference(schedule.inventoryItem),
                owningCompanyId: this.owningCompanyId() || 0
            })),
            items: validItems.map(({ missingItemReference, importGroupKey, brandId, brandName, ...item }) => ({
                ...item,
                inventoryItem: this.warehousingItemReference(item.inventoryItem),
                sourceWarehouse: undefined,
                sourceWarehousingWarehouseId: item.sourceWarehouse?.warehousingWarehouseId || item.sourceWarehouse?.id,
                sourceWarehousingWarehouseName: item.sourceWarehouse?.name,
                quantity: item.quantity || 1,
                owningCompanyId: this.owningCompanyId() || 0
            }))
        };

        this.posmService.createProject(newProject).subscribe({
            next: (val) => {
                this.isSaving.set(false);
                this.messageService.add({ severity: 'success', summary: 'Execution Created', detail: `POSM execution ${val.projectNumber} created successfully` });
                this.linkProjectRequirementFulfillment(val)
                    .add(() => setTimeout(() => this.router.navigate(['/posm/executions', val.id]), 1500));
            },
            error: (err) => {
                console.error('Submission failed', err);
                this.isSaving.set(false);
                this.messageService.add({ severity: 'error', summary: 'POSM execution not created',
                    detail: err?.error?.message || err?.error?.detail || 'Failed to create POSM execution' });
            }
        });
    }

    chooseLocation(store: PosmProjectStore, value: number): void {
        store.location ||= {};
        store.locationMatchAction = value === -1 ? 'CREATE' : 'REUSE';
        store.location.id = value === -1 ? undefined : value;
        const storeIndex = this.uploadedStores().indexOf(store);
        store.locationMatchSelectionFingerprint = value === -1 ? undefined
            : this.locationMatches()[storeIndex]?.candidates.find(candidate => candidate.id === value)?.fingerprint;
    }

    locationOptions(match: LocationMatchPreview): { label: string; value: number }[] {
        return [
            ...match.candidates.map(candidate => ({ value: candidate.id,
                label: [candidate.name, candidate.reference, candidate.city, candidate.retailer, candidate.address]
                    .filter(Boolean).join(' | ') })),
            { value: -1, label: 'Create a new location' }
        ];
    }

    searchAdditionalLocations(index: number, search: string): void {
        const companyId = this.owningCompanyId();
        if (!companyId || search.trim().length < 2) return;
        this.posmService.previewLocationMatches(companyId, [{ name: search.trim() }]).subscribe({
            next: results => {
                const matches = [...this.locationMatches()];
                const current = matches[index];
                if (!current) return;
                const candidates = [...current.candidates];
                for (const candidate of results[0]?.candidates || []) {
                    const existingIndex = candidates.findIndex(row => row.id === candidate.id);
                    if (existingIndex < 0) candidates.push(candidate); else candidates[existingIndex] = candidate;
                }
                matches[index] = { ...current, candidates };
                this.locationMatches.set(matches);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Location search failed', detail: 'Unable to search for more locations.' })
        });
    }

    private locationReviewComplete(): boolean {
        if (this.isParsing() || this.uploadedStores().some(store => !store.locationMatchAction || !store.locationMatchFingerprint
                || (store.locationMatchAction === 'REUSE' && !store.locationMatchSelectionFingerprint))) {
            this.messageService.add({ severity: 'warn', summary: 'Review locations', detail: 'Choose a match or create a new location for each store.' });
            return false;
        }
        return true;
    }

    private crmClientReference(client: CrmClient | null | undefined): CrmClient | undefined {
        return client?.id ? ({ id: client.id } as CrmClient) : undefined;
    }

    private crmMerchandiserReference(merchandiser: CrmMerchandiser | null | undefined): CrmMerchandiser | undefined {
        return merchandiser?.id ? ({ id: merchandiser.id } as CrmMerchandiser) : undefined;
    }

    private customerReference(customer: Customer | null | undefined): Customer | undefined {
        return customer?.id ? ({ id: customer.id } as Customer) : undefined;
    }

    private locationReference(location: any): any {
        return location?.id ? { id: location.id } : undefined;
    }

    private companyReference(company: any): any {
        return company?.id ? { id: company.id } : undefined;
    }

    private warehousingItemReference(item: WarehousingItem | null | undefined): any {
        return item?.id ? { id: item.id } : undefined;
    }

    private toWarehousingItemOption(item: WarehousingItem): WarehousingItemOption {
        return {
            ...item,
            displayName: this.itemDisplayLabel(item),
            itemMeta: this.itemMetaLabel(item),
            sizeLabel: this.itemSizeLabel(item)
        };
    }

    private toWarehouseOptions(rows: WarehousingMasterData[]): WarehousingWarehouseOption[] {
        return (rows || [])
            .map(row => ({
                id: row.id!,
                name: this.warehousingWarehouseLabel(row),
                code: row.code,
                shortDescription: row.code || row.supplierWarehouseName || row.locationName,
                externalLocationReference: row.code,
                warehousingWarehouseId: row.id!,
                warehouseOwnershipType: row.warehouseOwnershipType,
                supplierClientId: row.supplierClientId,
                supplierWarehouseName: row.supplierWarehouseName
            }))
            .filter(row => !!row.id);
    }

    private warehousingWarehouseLabel(row: WarehousingMasterData): string {
        const primary = row.name || row.code || row.supplierWarehouseName || `Warehouse #${row.id}`;
        const reference = row.code && !primary.includes(row.code) ? ` - ${row.code}` : '';
        return `${primary}${reference}`;
    }

    private clientScopedWarehousingItems(): WarehousingItemOption[] {
        const clientId = this.createProjectForm?.value?.crmClient?.id as number | undefined;
        const accountId = this.createProjectForm?.value?.clientAccountId as number | null | undefined;
        if (!clientId) {
            return this.warehousingItemOptions();
        }
        const byClient = this.warehousingItemOptions().filter(item => item.crmClientId === clientId);
        if (!accountId) {
            return byClient;
        }
        const byAccount = byClient.filter(item => item.crmAccountId === accountId);
        return byAccount.length ? byAccount : byClient;
    }

    availableBrandsForSelectedClient(): WarehousingMasterData[] {
        const clientBrandIds = new Set(
            this.clientScopedWarehousingItems()
                .map(item => item.brandId)
                .filter((value): value is number => value != null)
        );
        return this.brandOptions().filter(brand => !!brand.id && clientBrandIds.has(brand.id));
    }

    brandOptionsForItem(item: PosmProjectItem): WarehousingMasterData[] {
        const available = this.availableBrandsForSelectedClient();
        const selectedBrandId = item.brandId;
        if (!selectedBrandId) {
            return available;
        }
        return available.some(brand => brand.id === selectedBrandId)
            ? available
            : this.brandOptions().filter(brand => brand.id === selectedBrandId).concat(available);
    }

    itemAvailability(item: PosmProjectItem): { available: number | null; required: number; enough: boolean; text: string; severity: 'success' | 'danger' | 'secondary' } {
        if (this.isThirdPartyVirtualWarehouse(item)) {
            return {
                available: null,
                required: this.toPositiveQuantity(item.quantity),
                enough: true,
                text: 'Third-party virtual warehouse quantity is recorded only. No stock-ledger reservation is checked.',
                severity: 'secondary'
            };
        }
        if (this.isSupplierWarehouse(item)) {
            return {
                available: null,
                required: this.toPositiveQuantity(item.quantity),
                enough: false,
                text: 'Supplier warehouse stock is not tracked here',
                severity: 'secondary'
            };
        }
        const required = this.toPositiveQuantity(item.quantity);
        const warehouseId = item.sourceWarehouse?.warehousingWarehouseId;
        const itemId = item.inventoryItem?.id;
        if (!warehouseId || !itemId) {
            return { available: null, required, enough: false, text: 'Select warehouse and item to check stock', severity: 'secondary' };
        }
        const available = this.stockSummary()
            .filter(row => row.warehouseId === warehouseId && row.itemId === itemId)
            .reduce((sum, row) => sum + (Number(row.availableQuantity) || 0), 0);
        const enough = available >= required;
        const warehouseName = item.sourceWarehouse?.name || 'selected warehouse';
        const itemName = item.inventoryItem?.itemName || 'selected item';
        return {
            available,
            required,
            enough,
            text: enough
                ? `${available} available in ${warehouseName}`
                : `Not enough quantity for ${itemName} in ${warehouseName}. ${available} available, ${required} required.`,
            severity: enough ? 'success' : 'danger'
        };
    }

    itemOptionsForBrand(item: PosmProjectItem): WarehousingItemOption[] {
        const brandId = item.brandId;
        const selectedItemId = item.inventoryItem?.id;
        const scopedItems = this.clientScopedWarehousingItems();
        const filtered = scopedItems.filter(option => {
            if (!option.id) {
                return false;
            }
            if (option.id === selectedItemId) {
                return true;
            }
            if (!brandId) {
                return true;
            }
            return option.brandId === brandId;
        });
        if (filtered.length) {
            return filtered;
        }
        return selectedItemId
            ? this.warehousingItemOptions().filter(option => option.id === selectedItemId)
            : scopedItems;
    }

    onSourceWarehouseChanged(item: PosmProjectItem): void {
        if (!item.importGroupKey) {
            return;
        }
        const warehouse = item.sourceWarehouse as WarehousingWarehouseOption | null;
        if (!warehouse?.warehousingWarehouseId || !warehouse.code) {
            this.messageService.add({ severity: 'error', summary: 'Warehouse required',
                detail: 'Select a warehouse with a code for imported delivery rows.' });
            return;
        }
        const rows = this.deliveryScheduleRows().map(row => row.importGroupKey === item.importGroupKey
            ? { ...row, locatedWarehouseId: warehouse.warehousingWarehouseId,
                locatedWarehouseCode: warehouse.code, locatedWarehouseName: warehouse.name }
            : row);
        this.mergeImportedItemGroup(item, rows);
    }

    isSupplierWarehouse(item: PosmProjectItem | null | undefined): boolean {
        return !!item?.sourceWarehouse?.supplierClientId;
    }

    isThirdPartyVirtualWarehouse(item: PosmProjectItem | null | undefined): boolean {
        return `${item?.sourceWarehouse?.warehouseOwnershipType || ''}`.toUpperCase() === 'THIRD_PARTY_VIRTUAL';
    }

    onBrandChanged(item: PosmProjectItem): void {
        const selectedBrand = this.brandOptions().find(brand => brand.id === item.brandId);
        item.brandName = selectedBrand?.name;
        if (!item.inventoryItem?.id) {
            return;
        }
        const allowed = this.itemOptionsForBrand(item).some(option => option.id === item.inventoryItem?.id);
        if (!allowed) {
            item.inventoryItem = null;
            item.missingItemReference = undefined;
        }
    }

    onWarehousingItemChanged(item: PosmProjectItem): void {
        const selectedItem = item.inventoryItem as WarehousingItemOption | null | undefined;
        if (!selectedItem?.id) {
            return;
        }
        item.brandId = selectedItem.brandId;
        item.brandName = selectedItem.brandName;
    }

    private mergeImportedItemGroup(item: PosmProjectItem, rows: PosmDeliverySchedule[]): void {
        const duplicate = this.projectItems().find(other => other !== item && !!other.importGroupKey
            && (item.inventoryItem?.id
                ? other.inventoryItem?.id === item.inventoryItem.id
                : !other.inventoryItem?.id && other.missingItemReference === item.missingItemReference)
            && other.sourceWarehouse?.warehousingWarehouseId === item.sourceWarehouse?.warehousingWarehouseId);
        if (duplicate) {
            duplicate.quantity = this.toPositiveQuantity(duplicate.quantity) + this.toPositiveQuantity(item.quantity);
            rows = rows.map(row => row.importGroupKey === item.importGroupKey
                ? { ...row, importGroupKey: duplicate.importGroupKey }
                : row);
            this.projectItems.set(this.projectItems().filter(other => other !== item));
        } else {
            this.projectItems.set([...this.projectItems()]);
        }
        this.deliveryScheduleRows.set(rows);
    }

    private itemNameFromStand(standName: string, standSize?: string): string {
        const name = `${standName || ''}`.trim();
        const size = `${standSize || ''}`.trim();
        if (!size || this.normalizeReference(name).includes(this.normalizeReference(size))) {
            return name;
        }
        return `${name} ${size}`.trim();
    }

    warehousingClientName(id?: number): string {
        return this.crmClients().find(client => client.id === id)?.companyName || '-';
    }

    warehousingAccountName(id?: number): string {
        return this.accounts().find(account => account.id === id)?.companyName || '-';
    }

    warehousingItemSizeLabel(item: WarehousingItem | null | undefined): string {
        return item ? this.itemSizeLabel(item) : '-';
    }

    private itemDisplayLabel(item: WarehousingItem): string {
        return `${item.itemName} | ${this.warehousingClientName(item.crmClientId)} | ${this.warehousingAccountName(item.crmAccountId)} | ${this.itemSizeLabel(item)}`;
    }

    private itemMetaLabel(item: WarehousingItem): string {
        return [
            item.itemNumber || item.skuNumber || item.customerItemNumber || 'No item #',
            this.warehousingClientName(item.crmClientId),
            this.warehousingAccountName(item.crmAccountId)
        ].join(' | ');
    }

    private itemSizeLabel(item: WarehousingItem): string {
        const length = this.formatDimension(item.lengthMeters);
        const breadth = this.formatDimension(item.breadthMeters);
        const height = this.formatDimension(item.heightMeters);
        if (length === '-' && breadth === '-' && height === '-') {
            return 'No size';
        }
        return `${length} x ${breadth} x ${height} m`;
    }

    private formatDimension(value?: number): string {
        return value === null || value === undefined
            ? '-'
            : new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
    }

    private buildUploadedStores(rows: PosmDeliverySchedule[], companyId: number): PosmProjectStore[] {
        const stores = new Map<string, PosmProjectStore>();
        rows.forEach(row => {
            const key = this.scheduleStoreKey(row);
            const current = stores.get(key) || ({ owningCompanyId: companyId } as PosmProjectStore);
            const location = this.buildScheduleLocation(row, current.location);
            current.location = location;
            current.storeNumber = this.preferredText(current.storeNumber, row.storeNumber, row.location?.externalLocationReference);
            current.storeName = this.preferredText(current.storeName, row.storeName, this.scheduleLocationDisplayName(row), row.jobName, location.name);
            current.city = this.preferredText(current.city, row.city, location.shortDescription);
            current.contactPersonName = this.preferredText(current.contactPersonName, row.customerName, row.client, row.jobName);
            current.contactPersonPhone = this.preferredText(current.contactPersonPhone, row.contactNumber);
            current.address = this.preferredText(current.address, row.comment, row.city, location.longDescription);
            current.plannedDate = current.plannedDate || row.installationDate || row.statusDate || row.jobDate || undefined;
            current.status = this.preferredText(current.status, 'PENDING');
            current.comments = this.preferredText(current.comments, row.comment);
            current.storeCode = this.preferredText(current.storeCode, row.jobNumber);
            current.kasName = this.preferredText(current.kasName, row.jobName);
            current.storeFormat = this.preferredText(current.storeFormat, row.standSize);
            current.truckName = this.preferredText(current.truckName, row.truck);
            current.deliveryStatus = this.preferredText(current.deliveryStatus, row.status);
            current.approvalStatus = current.approvalStatus || 'PENDING';
            stores.set(key, current);
        });
        return Array.from(stores.values());
    }

    private buildDeliveryScheduleRows(
        rows: PosmDeliverySchedule[],
        stores: PosmProjectStore[],
        companyId: number,
        rowItemMatches: Map<string, WarehousingItemOption>
    ): PosmDeliverySchedule[] {
        const storeIndex = new Map<string, PosmProjectStore>();
        stores.forEach(store => storeIndex.set(this.scheduleStoreKeyFromStore(store), store));
        const schedules: PosmDeliverySchedule[] = [];
        const schedulesByHash = new Map<string, PosmDeliverySchedule>();
        rows.forEach(row => {
            const store = storeIndex.get(this.scheduleStoreKey(row));
            const matchedItem = rowItemMatches.get(this.scheduleRowKey(row)) || row.inventoryItem;
            const schedule = {
                ...row,
                owningCompanyId: companyId,
                location: store?.location || row.location,
                inventoryItem: matchedItem,
                inventoryItemId: matchedItem?.id,
                storeNumber: this.preferredText(row.storeNumber, store?.storeNumber),
                storeName: this.preferredText(row.storeName, store?.storeName),
                city: this.preferredText(row.city, store?.city),
                contactNumber: this.preferredText(row.contactNumber, store?.contactPersonPhone),
                installationDate: row.installationDate || store?.plannedDate || row.statusDate || row.jobDate || undefined,
                status: this.preferredText(row.status, 'PENDING')
            };
            const existing = row.sourceRowHash ? schedulesByHash.get(row.sourceRowHash) : undefined;
            if (existing) {
                existing.standQty = this.toPositiveQuantity(existing.standQty) + this.toPositiveQuantity(schedule.standQty);
            } else {
                schedules.push(schedule);
                if (row.sourceRowHash) schedulesByHash.set(row.sourceRowHash, schedule);
            }
        });
        return schedules;
    }

    private buildScheduleLocation(row: PosmDeliverySchedule, existing?: any): any {
        const name = this.preferredText(existing?.name, this.scheduleLocationDisplayName(row), row.jobName, row.storeNumber, 'POSM Store');
        const externalReference = this.preferredText(existing?.externalLocationReference, row.storeNumber, row.storeName, row.jobNumber);
        return {
            ...(existing || {}),
            name,
            shortDescription: this.preferredText(existing?.shortDescription, row.city, row.jobName, name),
            longDescription: this.preferredText(existing?.longDescription, row.comment, row.jobName, 'POSM store imported from delivery schedule'),
            telephoneNumber: this.preferredText(existing?.telephoneNumber, row.contactNumber),
            externalLocationReference: externalReference,
            locationType: this.preferredText(existing?.locationType, 'POSM_STORE'),
            location: existing?.location || null
        };
    }

    private scheduleStoreKey(row: PosmDeliverySchedule): string {
        return this.normalizeReference(this.preferredText(row.storeNumber, row.storeName, row.customerName, row.jobName));
    }

    private scheduleStoreKeyFromStore(store: PosmProjectStore): string {
        return this.normalizeReference(this.preferredText(store.storeNumber, store.storeName, store.location?.externalLocationReference, store.location?.name));
    }

    private scheduleRowKey(row: PosmDeliverySchedule): string {
        return this.normalizeReference(this.preferredText(
            row.sourceRowHash,
            row.sourceSheetName && row.sourceRowNumber != null ? `${row.sourceSheetName}:${row.sourceRowNumber}` : undefined,
            row.storeNumber,
            row.storeName,
            row.uCode,
            row.itemNumber,
            row.itemSku,
            row.customerItemNumber,
            row.standName,
            row.contactNumber
        ));
    }

    private preferredText(...values: Array<string | null | undefined>): string | undefined {
        for (const value of values) {
            if (value != null) {
                const trimmed = `${value}`.trim();
                if (trimmed.length) {
                    return trimmed;
                }
            }
        }
        return undefined;
    }

    storeLocationLabel(store: PosmProjectStore): string {
        return store.location?.name || store.storeName || (store.storeNumber ? `Store ${store.storeNumber}` : 'Store Location');
    }

    storeLocationReference(store: PosmProjectStore): string {
        return store.location?.externalLocationReference || store.storeNumber || '-';
    }

    storeAddress(store: PosmProjectStore): string {
        return store.address || store.location?.longDescription || store.location?.shortDescription || '-';
    }

    storeCoordinateStatus(store: PosmProjectStore): string {
        return store.location?.location?.latitude != null && store.location?.location?.longitude != null
            ? 'Coordinates set'
            : 'Coordinates missing';
    }

    storeCoordinateSeverity(store: PosmProjectStore): string {
        return store.location?.location?.latitude != null && store.location?.location?.longitude != null
            ? 'success'
            : 'warning';
    }

    storeItemNumbers(store: PosmProjectStore): string {
        const storeKey = this.scheduleStoreKeyFromStore(store);
        const itemNumbers = new Set(
            this.deliveryScheduleRows()
                .filter(row => this.scheduleStoreKey(row) === storeKey)
                .map(row => row.itemNumber || row.itemSku || row.inventoryItem?.itemNumber || row.inventoryItem?.skuNumber)
                .filter(number => !!number)
        );
        return Array.from(itemNumbers).join(', ') || '-';
    }

    storeItemNames(store: PosmProjectStore): string {
        const storeKey = this.scheduleStoreKeyFromStore(store);
        const labels = new Set(
            this.deliveryScheduleRows()
                .filter(row => this.scheduleStoreKey(row) === storeKey)
                .map(row => this.scheduleItemLabel(row))
                .filter(label => !!label)
        );
        return Array.from(labels).join(', ') || '-';
    }

    crmClientDisplayName(client: CrmClient | null | undefined): string {
        if (!client) return '';
        return client.clientNumber ? `${client.clientNumber} - ${client.companyName}` : client.companyName;
    }

    customerDisplayName(customer: Customer | null | undefined): string {
        if (!customer) return '';
        const contact = customer.emailAddress || customer.phoneNumber;
        return contact ? `${customer.name} - ${contact}` : customer.name;
    }

    merchandiserDisplayName(merchandiser: CrmMerchandiser | null | undefined): string {
        if (!merchandiser) return '';
        const reference = merchandiser.merchandiserNumber || merchandiser.employeeCode;
        return reference ? `${reference} - ${merchandiser.name}` : merchandiser.name;
    }

    private crmClientContactDisplayName(contact: CrmClientCommunication | PosmClientContactOption | null | undefined): string {
        if (!contact) return '';
        const row = contact as any;
        const email = row.emailAddress || row.email;
        const phone = row.phoneNumber || row.phone || row.mobile || row.mobileNumber;
        const detail = email || phone;
        return detail ? `${row.name || 'Client Contact'} - ${detail}` : (row.name || 'Client Contact');
    }

    private loadCrmClientContacts(client: CrmClient | null | undefined): void {
        if (!client?.id) {
            this.isClientContactsLoading.set(false);
            this.crmClientContacts.set([]);
            this.createProjectForm.patchValue({ clientContact: null }, { emitEvent: false });
            return;
        }
        this.isClientContactsLoading.set(true);
        this.crmService.getClientMasterCommunications(client.id).pipe(
            finalize(() => this.isClientContactsLoading.set(false))
        ).subscribe({
            next: contacts => {
                const options = (contacts || []).map(contact => this.toClientContactOption(contact));
                this.crmClientContacts.set(options);
                const selected = this.createProjectForm.value.clientContact as PosmClientContactOption | null;
                const resolved = selected?.id
                    ? options.find(option => option.id === selected.id) || null
                    : null;
                this.createProjectForm.patchValue({
                    clientContact: resolved || this.resolveDefaultClientContact(client, this.createProjectForm.value.clientAccountId)
                }, { emitEvent: false });
            },
            error: () => {
                this.crmClientContacts.set([]);
                this.createProjectForm.patchValue({ clientContact: null }, { emitEvent: false });
            }
        });
    }

    private resolveDefaultClientContact(client: CrmClient | null | undefined, _accountId: number | null | undefined): PosmClientContactOption | null {
        const contacts = this.crmClientContacts();
        if (!contacts.length) {
            return null;
        }
        return contacts.find(contact => contact.primaryRecord) || contacts[0] || null;
    }

    private toClientContactOption(contact: CrmClientCommunication): PosmClientContactOption {
        return {
            id: contact.id ?? null,
            name: contact.name || 'Client Contact',
            emailAddress: contact.email || '',
            phoneNumber: contact.phone || '',
            mobileNumber: contact.mobile || '',
            primaryRecord: !!contact.primaryRecord,
            displayName: this.crmClientContactDisplayName(contact)
        };
    }

    private resolveUploadedItems(data: any[], companyId: number): {
        items: PosmProjectItem[];
        rowItemMatches: Map<string, WarehousingItemOption>;
        missingUCodes: string[];
        inactiveUCodes: string[];
        duplicateUCodes: string[];
        legacyMissingReferences: string[];
    } {
        const grouped = new Map<string, PosmProjectItem>();
        const rowItemMatches = new Map<string, WarehousingItemOption>();
        const missingUCodes = new Set<string>();
        const inactiveUCodes = new Set<string>();
        const duplicateUCodes = new Set<string>();
        const legacyMissingReferences = new Set<string>();

        data.forEach(row => {
            const itemNumber = `${row.itemNumber || row.itemSku || ''}`.trim();
            const customerItemNumber = `${row.customerItemNumber || ''}`.trim();
            const standName = `${row.standName || ''}`.trim();
            const standSize = `${row.standSize || ''}`.trim();
            const uCode = `${row.uCode || ''}`.trim();
            const warehouseId = Number(row.locatedWarehouseId);
            const sourceWarehouse = this.warehouseOptions().find(option => option.warehousingWarehouseId === warehouseId)
                || (warehouseId ? {
                    id: warehouseId, warehousingWarehouseId: warehouseId,
                    name: `${row.locatedWarehouseName || 'Warehouse'} - ${row.locatedWarehouseCode}`,
                    code: row.locatedWarehouseCode,
                    shortDescription: row.locatedWarehouseCode,
                    externalLocationReference: row.locatedWarehouseCode
                } as WarehousingWarehouseOption : null);
            if (!uCode && !itemNumber && !customerItemNumber && !standName) return;

            // The API preview is authoritative. Never re-resolve an explicit
            // U Code against an asynchronously loaded browser item list.
            const matchedItem = row.inventoryItem?.id
                ? this.toWarehousingItemOption(row.inventoryItem)
                : null;
            if (matchedItem?.id) {
                rowItemMatches.set(this.scheduleRowKey(row), matchedItem);
                this.addResolvedWarehousingItemOption(matchedItem);
            }
            const quantity = this.toPositiveQuantity(row.standQty ?? row.quantity);
            const missingReference = uCode
                ? `U Code ${uCode}`
                : this.itemIdentifierLabel(standName, standSize, itemNumber || customerItemNumber);
            const key = matchedItem?.id
                ? `item:${matchedItem.id}:warehouse:${warehouseId}`
                : `missing:${this.normalizeReference(missingReference)}:warehouse:${warehouseId}`;
            row.importGroupKey = key;
            const existing = grouped.get(key);
            if (existing) {
                existing.quantity = (existing.quantity || 0) + quantity;
                return;
            }

            if (!matchedItem) {
                switch (row.itemResolutionStatus) {
                    case 'U_CODE_MISSING':
                        missingUCodes.add(uCode || missingReference);
                        break;
                    case 'U_CODE_INACTIVE':
                        inactiveUCodes.add(uCode || missingReference);
                        break;
                    case 'U_CODE_DUPLICATE':
                        duplicateUCodes.add(uCode || missingReference);
                        break;
                    default:
                        legacyMissingReferences.add(missingReference);
                        break;
                }
            }
            grouped.set(key, {
                brandId: matchedItem?.brandId,
                brandName: matchedItem?.brandName,
                inventoryItem: matchedItem || null,
                quantity,
                sourceWarehouse,
                importGroupKey: key,
                missingItemReference: matchedItem ? undefined : missingReference,
                owningCompanyId: companyId
            });
        });

        return {
            items: Array.from(grouped.values()),
            rowItemMatches,
            missingUCodes: Array.from(missingUCodes),
            inactiveUCodes: Array.from(inactiveUCodes),
            duplicateUCodes: Array.from(duplicateUCodes),
            legacyMissingReferences: Array.from(legacyMissingReferences)
        };
    }

    private addResolvedWarehousingItemOption(item: WarehousingItemOption): void {
        if (this.warehousingItemOptions().some(option => option.id === item.id)) {
            return;
        }
        this.warehousingItemOptions.set([item, ...this.warehousingItemOptions()]);
    }

    private showItemResolutionWarnings(resolvedItems: {
        missingUCodes: string[];
        inactiveUCodes: string[];
        duplicateUCodes: string[];
        legacyMissingReferences: string[];
    }): void {
        const warnings: Array<{ values: string[]; detail: string }> = [
            { values: resolvedItems.missingUCodes, detail: 'U Code(s) were not found for this owning company.' },
            { values: resolvedItems.inactiveUCodes, detail: 'U Code(s) only match inactive warehouse item(s).' },
            { values: resolvedItems.duplicateUCodes, detail: 'U Code(s) match multiple active warehouse items.' },
            { values: resolvedItems.legacyMissingReferences, detail: 'legacy item reference(s) were not found in active Warehousing.' }
        ];
        warnings.filter(warning => warning.values.length).forEach(warning => {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warehousing Items',
                detail: `${warning.values.length} ${warning.detail} ${warning.values.join(', ')}`
            });
        });
    }

    private itemIdentifierLabel(standName: string, standSize: string, reference: string): string {
        const standLabel = this.itemNameFromStand(standName, standSize);
        return standLabel || reference || 'Unmapped item';
    }

    private scheduleItemLabel(row: PosmDeliverySchedule): string {
        const itemName = this.preferredText(row.standName, row.inventoryItem?.itemName);
        const customerItemNumber = this.preferredText(row.customerItemNumber, row.inventoryItem?.customerItemNumber);
        if (itemName && customerItemNumber) {
            return `${itemName} | Customer ${customerItemNumber}`;
        }
        return itemName || customerItemNumber || '';
    }

    private normalizeReference(value: unknown): string {
        return `${value || ''}`.trim().toLowerCase();
    }

    private scheduleLocationDisplayName(row: PosmDeliverySchedule): string | undefined {
        const explicitName = this.preferredText(row.storeName);
        if (explicitName) {
            return explicitName;
        }
        const parts = [
            this.preferredText(row.customerName),
            this.preferredText(row.storeNumber),
            this.preferredText(row.city)
        ].filter((value, index, all): value is string => !!value && all.indexOf(value) === index);
        if (parts.length) {
            return parts.join(' | ');
        }
        return this.preferredText(row.jobName, row.storeNumber, 'POSM Store');
    }

    private normalizePhone(value: unknown): string {
        return `${value || ''}`.replace(/\D+/g, '');
    }

    private toPositiveQuantity(value: unknown): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    private requirementProjectId(): number {
        const query = this.route.snapshot.queryParamMap;
        return Number(query.get('projectId') || query.get('logisticsProjectId') || 0);
    }

    private linkProjectRequirementFulfillment(project: PosmProject) {
        const query = this.route.snapshot.queryParamMap;
        const projectId = this.requirementProjectId();
        const requirementId = Number(query.get('requirementId') || 0);
        const owningCompanyId = this.owningCompanyId() || this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
        if (!projectId || !requirementId || !owningCompanyId || !project.id) {
            return { add: (callback: () => void) => callback() };
        }
        const queryQuantity = Number(query.get('quantity') || 0);
        const projectQuantity = this.projectItems().reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        const fulfilledQuantity = queryQuantity > 0 ? queryQuantity : (projectQuantity > 0 ? projectQuantity : 1);
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: query.get('targetResourceType') || 'POSM_PROJECT',
            targetResourceId: project.id,
            sourceModule: 'POSM',
            sourceReference: project.projectNumber || project.projectName || `POSM Project #${project.id}`,
            fulfilledQuantity,
            fulfillmentStatus: 'CREATED',
            status: 'ACTIVE'
        }).subscribe({
            error: () => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: 'POSM execution was saved, but the requirement fulfillment link could not be updated.'
            })
        });
    }

    private syncProjectItemsToClientContext(): void {
        const allowedBrandIds = new Set(
            this.availableBrandsForSelectedClient()
                .map(brand => brand.id)
                .filter((value): value is number => value != null)
        );
        const scopedItemIds = new Set(
            this.clientScopedWarehousingItems()
                .map(item => item.id)
                .filter((value): value is number => value != null)
        );
        const defaultBrand = this.availableBrandsForSelectedClient().length === 1
            ? this.availableBrandsForSelectedClient()[0]
            : null;

        const nextItems = this.projectItems().map(item => {
            const next = { ...item };
            if (next.brandId && !allowedBrandIds.has(next.brandId)) {
                next.brandId = undefined;
                next.brandName = undefined;
                next.inventoryItem = null;
            }
            if (next.inventoryItem?.id && !scopedItemIds.has(next.inventoryItem.id)) {
                next.inventoryItem = null;
            }
            if (!next.brandId && defaultBrand?.id) {
                next.brandId = defaultBrand.id;
                next.brandName = defaultBrand.name;
            }
            return next;
        });
        this.projectItems.set(nextItems);
    }

    private syncJobTypeGroupFromTypes(jobTypeIds: number[] | null | undefined): void {
        const selectedTypes = this.selectedJobTypes(jobTypeIds);
        const groups = Array.from(new Set(selectedTypes.map(jobType => this.jobTypeGroupValue(jobType)).filter(Boolean)));
        const group = groups.length === 1 ? groups[0] : this.selectedJobTypeGroup();
        if (this.createProjectForm.get('jobTypeGroup')?.value !== group) {
            this.createProjectForm.patchValue({ jobTypeGroup: group }, { emitEvent: false });
        }
        this.selectedJobTypeGroup.set(group);
    }

    private selectedJobTypes(jobTypeIds: number[] | null | undefined): PosmSetupData[] {
        const ids = Array.isArray(jobTypeIds) ? jobTypeIds : [];
        return ids
            .map(jobTypeId => this.jobTypeOptions().find(row => row.id === jobTypeId) || null)
            .filter((row): row is PosmSetupData => !!row);
    }

    private jobTypesForGroup(group: string | null | undefined): PosmSetupData[] {
        if (!group) {
            return this.jobTypeOptions();
        }
        return this.jobTypeOptions().filter(row => this.jobTypeGroupValue(row) === group);
    }

    private normalizeJobTypeOptions(rows: PosmSetupData[]): PosmSetupData[] {
        const deduped = new Map<string, PosmSetupData>();
        rows
            .filter(row => row?.isActive !== false)
            .forEach(row => {
                const code = this.normalizedJobTypeCode(row);
                const name = this.normalizedJobTypeName(row);
                const key = this.normalizeReference(code || name);
                if (!key || deduped.has(key)) {
                    return;
                }
                deduped.set(key, {
                    ...row,
                    code,
                    name,
                    category: this.jobTypeGroupValue(row)
                });
            });
        return Array.from(deduped.values()).sort((left, right) => {
            const groupCompare = this.jobTypeGroupSortOrder(this.jobTypeGroupValue(left))
                - this.jobTypeGroupSortOrder(this.jobTypeGroupValue(right));
            if (groupCompare !== 0) {
                return groupCompare;
            }
            return this.jobTypeSortOrder(left) - this.jobTypeSortOrder(right);
        });
    }

    private jobTypeGroupValue(jobType: PosmSetupData | null | undefined): string {
        const normalized = this.normalizeReference(jobType?.code || jobType?.name);
        if (normalized === 'installation' || normalized === 'delivery' || normalized === 'removal' || normalized === 'collection') {
            return 'Logistics';
        }
        if (normalized === 'refurbishment' || normalized === 'rebranding') {
            return 'Refurbishment and Rebranding';
        }
        if (normalized === 'site_survey' || normalized === 'site survey') {
            return 'Site Survey';
        }
        return jobType?.category || 'Project';
    }

    private normalizedJobTypeName(jobType: PosmSetupData | null | undefined): string {
        const normalized = this.normalizeReference(jobType?.code || jobType?.name);
        switch (normalized) {
            case 'installation':
                return 'Installation';
            case 'delivery':
                return 'Delivery';
            case 'removal':
                return 'Removal';
            case 'collection':
                return 'Collection';
            case 'refurbishment':
                return 'Refurbishment';
            case 'rebranding':
                return 'Rebranding';
            case 'site_survey':
            case 'site survey':
                return 'Site Survey';
            default:
                return jobType?.name || '';
        }
    }

    private normalizedJobTypeCode(jobType: PosmSetupData | null | undefined): string {
        const normalized = this.normalizeReference(jobType?.code || jobType?.name);
        switch (normalized) {
            case 'installation':
                return 'INSTALLATION';
            case 'delivery':
                return 'DELIVERY';
            case 'removal':
                return 'REMOVAL';
            case 'collection':
                return 'COLLECTION';
            case 'refurbishment':
                return 'REFURBISHMENT';
            case 'rebranding':
                return 'REBRANDING';
            case 'site_survey':
            case 'site survey':
                return 'SITE_SURVEY';
            default:
                return jobType?.code || jobType?.name || '';
        }
    }

    private jobTypeGroupSortOrder(group: string | null | undefined): number {
        switch (group) {
            case 'Logistics':
                return 1;
            case 'Refurbishment and Rebranding':
                return 2;
            case 'Site Survey':
                return 3;
            default:
                return 99;
        }
    }

    private jobTypeSortOrder(jobType: PosmSetupData): number {
        switch (this.normalizedJobTypeCode(jobType)) {
            case 'INSTALLATION':
                return 1;
            case 'REMOVAL':
                return 2;
            case 'COLLECTION':
                return 3;
            case 'DELIVERY':
                return 4;
            case 'REFURBISHMENT':
                return 5;
            case 'REBRANDING':
                return 6;
            case 'SITE_SURVEY':
                return 7;
            default:
                return 99;
        }
    }

    private handleDialogWarehouseItemCreated(created: WarehousingItem | undefined): void {
        if (!created?.id) {
            this.quickCreateTargetItem = null;
            return;
        }
        const option = this.toWarehousingItemOption(created);
        this.warehousingItemOptions.set([
            option,
            ...this.warehousingItemOptions().filter(row => row.id !== option.id)
        ]);
        if (this.quickCreateTargetItem) {
            this.quickCreateTargetItem.inventoryItem = option;
            this.quickCreateTargetItem.missingItemReference = undefined;
        }
        this.quickCreateTargetItem = null;
        this.messageService.add({
            severity: 'success',
            summary: 'Warehousing item created',
            detail: created.itemName || created.itemNumber || `Item #${created.id}`
        });
    }

    private handleDialogWarehouseCreated(created: WarehousingMasterData | undefined): void {
        if (!created?.id) {
            this.quickCreateTargetItem = null;
            return;
        }
        const option = this.toWarehouseOptions([created])[0];
        if (option) {
            this.warehouseOptions.set([
                option,
                ...this.warehouseOptions().filter(row => row.warehousingWarehouseId !== option.warehousingWarehouseId)
            ]);
            if (this.quickCreateTargetItem) {
                this.quickCreateTargetItem.sourceWarehouse = option;
            }
        }
        this.quickCreateTargetItem = null;
        this.messageService.add({
            severity: 'success',
            summary: 'Warehouse created',
            detail: created.name || created.code || `Warehouse #${created.id}`
        });
    }
}
